import useSWR from 'swr';
import { conversationService, isApiError } from '@dopomogai/api-client';
import type { ConversationSummaryDetail, UUID } from '@dopomogai/types';
import { AgentKitError } from '../types';

const summaryFetcher = async ([, conversationId]: [string, UUID]): Promise<ConversationSummaryDetail> => {
  const response = await conversationService.getConversationSummary(conversationId);
  if (isApiError(response)) {
    throw new AgentKitError(response.message || 'Failed to fetch conversation summary.', response);
  }
  return response;
};

export const useConversationSummary = (conversationId: UUID | null) => {
  const cacheKey = conversationId ? ['conversation-summary', conversationId] : null;
  const { data, error, isLoading, mutate } = useSWR<ConversationSummaryDetail, AgentKitError>(
    cacheKey,
    summaryFetcher,
  );

  const summarizeNow = async () => {
    if (!conversationId) {
      throw new AgentKitError('Conversation ID is required to generate summary.');
    }
    const res = await conversationService.generateSummary(conversationId);
    if (isApiError(res)) {
      throw new AgentKitError(res.message || 'Failed to generate summary.', res);
    }
    await mutate(); // refresh to reflect the new summary
    return res;
  };

  return {
    summary: data,
    error,
    isLoading,
    mutate,
    summarizeNow,
  };
};